import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CFormSelect,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCalendar, cilColumns, cilStar, cilChartLine, cilDollar, cilBadge } from '@coreui/icons'
import moment from 'moment'
import React, { useState } from 'react'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import { resolvePlayerImageUrl } from 'src/utils/imageUrl'

const SEGMENTS = [
  { key: 'summary', label: 'Summary' },
  { key: 'batting', label: 'Batting' },
  { key: 'bowling', label: 'Bowling' },
  { key: 'fielding', label: 'Fielding' },
]

const SUMMARY_COLUMNS = [
  { label: 'Date', filter: false, sorter: false, key: 'date' },
  { label: 'Opponent', filter: false, sorter: false, key: 'opponent_name' },
  { label: 'Round', filter: false, sorter: false, key: 'round' },
  { label: 'Fantasy Points', filter: false, sorter: false, key: 'fantasy_points' },
  { label: 'Runs', filter: false, sorter: false, key: 'rs' },
  { label: 'Wickets', filter: false, sorter: false, key: 'wks' },
  { label: 'Catches', filter: false, sorter: false, key: 'cs' },
]

const BATTING_COLUMNS = [
  { label: 'Date', filter: false, sorter: false, key: 'date' },
  { label: 'Opponent', filter: false, sorter: false, key: 'opponent_name' },
  { label: 'Runs', filter: false, sorter: false, key: 'rs' },
  { label: 'Fours', filter: false, sorter: false, key: 'fours' },
  { label: 'Sixes', filter: false, sorter: false, key: 'sixes' },
]

const BOWLING_COLUMNS = [
  { label: 'Date', filter: false, sorter: false, key: 'date' },
  { label: 'Opponent', filter: false, sorter: false, key: 'opponent_name' },
  { label: 'Overs', filter: false, sorter: false, key: 'overs' },
  { label: 'Maidens', filter: false, sorter: false, key: 'mdns' },
  { label: 'Wickets', filter: false, sorter: false, key: 'wks' },
]

const FIELDING_COLUMNS = [
  { label: 'Date', filter: false, sorter: false, key: 'date' },
  { label: 'Opponent', filter: false, sorter: false, key: 'opponent_name' },
  { label: 'Catches', filter: false, sorter: false, key: 'cs' },
  { label: 'Caught & Bowled/WK', filter: false, sorter: false, key: 'cwks' },
  { label: 'Stumpings', filter: false, sorter: false, key: 'sts' },
  { label: 'Run Outs (D/A)', filter: false, sorter: false, key: 'run_outs' },
]

const SEGMENT_COLUMNS = {
  summary: SUMMARY_COLUMNS,
  batting: BATTING_COLUMNS,
  bowling: BOWLING_COLUMNS,
  fielding: FIELDING_COLUMNS,
}

const FixturePlayerStatsTab = (props) => {
  const [statsData, setStatsData] = useState([])
  const [player, setPlayer] = useState(null)
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(false)
  const [segment, setSegment] = useState('summary')
  const [hiddenColumns, setHiddenColumns] = useState({})

  const handlePlayer = (e) => {
    const playerId = e.target.value
    if (!playerId) {
      setStatsData([])
      setPlayer(null)
      setSummary({})
      return
    }
    setLoading(true)
    PlayerService.playerStatsByFixture(playerId)
      .then((res) => {
        if (res.success) {
          setStatsData(res.data || [])
          setPlayer(res.player || null)
          setSummary(res.summary || {})
        }
        setLoading(false)
      })
      .catch(() => {
        ToastComponent('Failed to load fixture stats for this player', 'error')
        setLoading(false)
      })
  }

  const toggleColumn = (key) => {
    setHiddenColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const columns = SEGMENT_COLUMNS[segment].filter((col) => !hiddenColumns[col.key])
  const imgSrc = player ? resolvePlayerImageUrl(player) : null

  return (
    <>
      <CCard className="mb-4">
        <CCardBody>
          <CRow className="g-3 align-items-end mb-3">
            <CCol md={3}>
              <CFormSelect
                aria-label="Select player"
                name="player"
                onChange={handlePlayer}
                id="player"
              >
                <option value="">Select Player</option>
                {props.playerList &&
                  props.playerList.map((item, key) => (
                    <option value={item?.id} key={key}>
                      {item?.full_name}
                    </option>
                  ))}
              </CFormSelect>
            </CCol>
            <CCol md="auto">
              <CButton
                color="light"
                className="d-inline-flex p-0 rounded-2 overflow-hidden border"
                style={{ gap: 0 }}
              >
                {SEGMENTS.map((s) => (
                  <span
                    key={s.key}
                    role="button"
                    onClick={() => setSegment(s.key)}
                    className={`px-3 py-2 small ${
                      segment === s.key ? 'bg-primary-subtle text-primary fw-semibold' : 'text-body'
                    }`}
                  >
                    {s.label}
                  </span>
                ))}
              </CButton>
            </CCol>
            <CCol md="auto" className="ms-auto">
              <CDropdown alignment="end">
                <CDropdownToggle color="secondary" variant="outline">
                  <CIcon icon={cilColumns} className="me-1" />
                  Columns
                </CDropdownToggle>
                <CDropdownMenu className="p-3">
                  {SEGMENT_COLUMNS[segment].map((col) => (
                    <div className="form-check" key={col.key}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`fixture-col-${col.key}`}
                        checked={!hiddenColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <label className="form-check-label" htmlFor={`fixture-col-${col.key}`}>
                        {col.label}
                      </label>
                    </div>
                  ))}
                </CDropdownMenu>
              </CDropdown>
            </CCol>
          </CRow>

          {player && (
            <div className="d-flex flex-wrap align-items-center gap-4 border rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center">
                {imgSrc && (
                  <img src={imgSrc} alt="" width={44} height={44} className="rounded-circle me-2" />
                )}
                <div>
                  <div className="fw-semibold">{player.full_name}</div>
                  <div className="text-medium-emphasis small">
                    {player.team_name || '—'} · {player.position_name || '—'}
                  </div>
                </div>
              </div>
              <StatChip icon={cilCalendar} label="Fixtures" value={summary.fixtures_played ?? 0} />
              <StatChip
                icon={cilStar}
                label="Fantasy Points"
                value={(summary.total_fantasy_points ?? 0).toLocaleString()}
              />
              <StatChip
                icon={cilChartLine}
                label="Average per Fixture"
                value={summary.average_per_fixture ?? 0}
              />
              {/* <StatChip
                icon={cilBadge}
                label="Best Fixture"
                value={
                  summary.best_fixture_opponent
                    ? `vs ${summary.best_fixture_opponent} · ${summary.best_fixture_points} pts`
                    : '—'
                }
              /> */}
              <StatChip
                icon={cilDollar}
                label="Current Value"
                value={`$${Number(summary.current_value ?? 0).toFixed(2)}m`}
              />
            </div>
          )}

          {player && (
            <div className="text-medium-emphasis small mb-2">
              Each row represents one scored fixture for the selected player.
            </div>
          )}

          <div className="table-responsive">
            <CSmartTable
              columns={columns}
              scopedColumns={{
                date: (item) => <td>{moment(item.fixture?.start_date).format('D MMM YYYY')}</td>,
                opponent_name: (item) => <td>{item?.opponent_name || '—'}</td>,
                round: (item) => <td>{item?.round ?? '—'}</td>,
                fantasy_points: (item) => <td>{item.fantasy_points}</td>,
                rs: (item) => <td>{item?.rs}</td>,
                fours: (item) => <td>{item?.fours}</td>,
                sixes: (item) => <td>{item?.sixes}</td>,
                overs: (item) => <td>{item?.overs}</td>,
                mdns: (item) => <td>{item?.mdns}</td>,
                wks: (item) => <td>{item?.wks}</td>,
                cs: (item) => <td>{item?.cs}</td>,
                cwks: (item) => <td>{item?.cwks}</td>,
                sts: (item) => <td>{item?.sts}</td>,
                run_outs: (item) => <td>{(item?.rods || 0) + (item?.roas || 0)}</td>,
              }}
              items={statsData}
              loading={loading}
              noItemsLabel={player ? 'No record yet available.' : 'Select a player to begin.'}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
            />
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

const StatChip = ({ icon, label, value }) => (
  <div className="d-flex align-items-center gap-2">
    <CIcon icon={icon} className="text-medium-emphasis" />
    <div>
      <div className="text-medium-emphasis small">{label}</div>
      <div className="fw-semibold">{value}</div>
    </div>
  </div>
)

export default FixturePlayerStatsTab
